import { DefaultProjectorFn, MemoizedSelector, createFeatureSelector, createSelector } from "@ngrx/store";
import { CouponCampaignRow, EntityLoadError, PROGRAM_FEATURE_KEY, ProgramIntegrations, ProgramState, RewardListRow } from "./state";
import { parseClaimCodeLink } from "../../utils/get-claim-code-link";
import type { ProfileFormLocaleTexts } from "../../utils/profile-form/profile-form";
import { WALLET_FIELD_ENTITY_TYPE, walletFieldTranslationSources } from "../../utils/wallet-front-fields/wallet-field-translations";
import {
  ApiKey,
  CampaignDto,
  CustomerData,
  CustomerEventDto,
  Device,
  EntityTranslation,
  Integration,
  IntegrationFeature,
  Link,
  Location,
  Program,
  ProgramIntegration,
  ProgramLanguagesResponseDto,
  TemplateMetadata,
  SectionDto,
  SegmentDto,
  SubscriptionInfo,
  CouponInfoDto,
  CouponCounts,
  RewardDto,
  RewardWithCouponCodeCountsDto,
  EngagementFlowWithProgramEngagementFlowDto,
  UpsertTranslationDto,
  Boost,
} from "../../services/api";
import { RootState } from "..";
import { selectIframeIntegrationName } from "../app/selectors";
import { environment } from "../../../environments/environment";
import { CampaignType, SegmentId } from "../../enums";
import { BASE_FEATURES, MCP_INTEGRATION_NAME } from "../../constants";

const getState = createFeatureSelector<ProgramState>(PROGRAM_FEATURE_KEY);

export const selectLoading = createSelector(getState, state => state.loading);

export const selectLoadingCampaigns = createSelector(getState, state => state.loadingCampaigns);

export const selectLoadingTiers = createSelector(getState, state => state.loadingTiers);

export const selectAutoTranslating = createSelector(getState, state => state.autoTranslating);

export const selectFormError = createSelector(getState, state => state.formError);

export const selectCustomersSearchTerm = createSelector(getState, state => state.customersSearchTerm);

export const selectCouponsSearchTerm = createSelector(getState, state => state.couponsSearchTerm);

export const selectCouponsPaginationData = createSelector(getState, state => state.couponsPaginationData);

export const selectLoadingFullScreen = createSelector(getState, state => state.loadingFullScreen);

export const selectOnboardingStatus = createSelector(getState, state => state.onboardingStatus);

export const selectLoadingCreateSubscription = createSelector(getState, state => state.loadingCreateSubscription);

export const selectLoadingCancelSubscription = createSelector(getState, state => state.loadingCancelSubscription);

export const selectLoadingKlaviyoAuth = createSelector(getState, state => state.loadingKlaviyoAuth);

export const selectLoadingAnalytics = createSelector(getState, state => state.loadingAnalytics);

export const selectProgram = createSelector(getState, state => state.program);

// Shopify Plus-only features (checkout extensions, Multipass) stay hidden unless Shopify says the shop is on Plus
export const selectShopifyPlus = createSelector(selectProgram, program => program?.shopifyPlus === true);

export const selectProgramLinks = createSelector(getState, state => state.programLinks);

export const selectCampaignLinks = createSelector(getState, state => Object.values(state.campaignLinks));

export const selectProgramSections = createSelector(getState, state => state.programSections);

export const selectProgramTierNames = createSelector(getState, state => state.tierNames);

export const selectTiers = createSelector(getState, state => state.tiers);

export const selectApiKeys = createSelector(getState, state => Object.values(state.apiKeys));

export const selectApiKey = (id: string): MemoizedSelector<RootState, ApiKey | undefined, DefaultProjectorFn<ApiKey | undefined>> =>
  createSelector(getState, state => state.apiKeys[id]);

export const selectIntegrations = createSelector(getState, state => state.integrations);

export const selectSubscriptionInfo = createSelector(getState, state => state.subscriptionInfo);

export const selectSubscriptionInfoLoaded = createSelector(getState, state => state.subscriptionInfoLoaded);

export const selectPaymentRequiredReason = createSelector(getState, state => state.paymentRequiredReason);

export const selectSubscriptionInfoError = createSelector(getState, state => state.subscriptionInfoError);

// Only a failure that left us with no usage data at all is worth surfacing: a stale but
// persisted subscriptionInfo is still good enough to render the plan and open the modal.
export const selectHasUnloadableSubscriptionInfo = createSelector(
  selectSubscriptionInfo,
  selectSubscriptionInfoError,
  (subscriptionInfo, hasError) => hasError && !subscriptionInfo,
);

export const selectAvailableDomain = createSelector(getState, state => state.availableDomain);

export const selectAllowedDomains = createSelector(getState, state => Object.values(state.allowedDomains));

export const selectMainDomain = createSelector(getState, state =>
  Object.values(state.allowedDomains)
    .filter(d => !!d)
    .find(({ domain }) => domain !== `${environment.webAppUrl}/${state.program?.slug}`) ?? Object.values(state.allowedDomains)[0],
);

export const selectHasActiveSubscription = createSelector(
  getState,
  state => state.subscriptionInfo?.status === SubscriptionInfo.StatusEnum.Active,
);

// Claude (MCP) has no features: activating it (a program integration) is what connects it
export const isIntegrationConnected = (integration: Integration, programIntegrations: ProgramIntegrations): boolean =>
  Object.values(programIntegrations)?.some(
    programIntegration =>
      (programIntegration.enabledFeatures?.length || integration.name === MCP_INTEGRATION_NAME) &&
      programIntegration.integrationId === integration.id,
  );

export const selectIsIntegrationConnected = (integration: Integration): MemoizedSelector<RootState, boolean, DefaultProjectorFn<boolean>> =>
  createSelector(getState, state => isIntegrationConnected(integration, state.programIntegrations));

export const selectFilteredAndSortedIntegrations = createSelector(getState, selectIframeIntegrationName, (state, iframeIntegrationName) => {
  const filterIntegration = (integration: Integration): boolean => {
    const isMcp = integration.name === MCP_INTEGRATION_NAME;
    // Its card is titled Claude, so "claude" finds it as well
    const searchableName = isMcp ? `claude ${integration.name}` : integration.name;
    const matchesSearchTerm = state.integrationsSearchTerm
      ? searchableName?.toLowerCase().includes(state.integrationsSearchTerm.toLowerCase())
      : true;

    // Claude (MCP) has no features: its active catalog row alone lists it
    const matchesFeatures = isMcp || integration.integrationFeatures?.some(feature => BASE_FEATURES.includes(feature.feature.name));

    return integration.active && matchesSearchTerm && matchesFeatures;
  };

  const filterByName = (integration: Integration, namesToInclude: string[], namesToExclude: string[] = []): boolean => {
    const include = namesToInclude.length === 0 || namesToInclude.includes(integration.name);
    const exclude = namesToExclude.includes(integration.name);
    return include && !exclude && filterIntegration(integration);
  };

  const filteredIntegrations = state.integrations.filter(integration => {
    switch (iframeIntegrationName) {
      case Program.IntegrationEnum.Convercus:
        return filterByName(integration, [Program.IntegrationEnum.Convercus]);
      case Program.IntegrationEnum.Shopify:
        return filterByName(integration, [], [Program.IntegrationEnum.Convercus]);
      default:
        return filterByName(integration, [], [Program.IntegrationEnum.Shopify, Program.IntegrationEnum.ShopifyLoyaltyLion]);
    }
  });

  return filteredIntegrations.sort((a, b) => {
    const isConnectedA = isIntegrationConnected(a, state.programIntegrations);
    const isConnectedB = isIntegrationConnected(b, state.programIntegrations);

    if (isConnectedA && !isConnectedB) return -1;
    else if (!isConnectedA && isConnectedB) return 1;
    else return a.name.localeCompare(b.name);
  });
});

// The integrations page lists connected integrations apart from the ones that can still be connected
export const selectIntegrationsByConnection = createSelector(selectFilteredAndSortedIntegrations, getState, (integrations, state) => ({
  connected: integrations.filter(integration => isIntegrationConnected(integration, state.programIntegrations)),
  available: integrations.filter(integration => !isIntegrationConnected(integration, state.programIntegrations)),
}));

export const selectProgramIntegrations = createSelector(getState, state => Object.values(state.programIntegrations));

export const selectMcpSettings = createSelector(getState, state => state.mcpSettings);

export const selectIntegrationsSearchTerm = createSelector(getState, state => state.integrationsSearchTerm);

export const selectAnalytics = createSelector(getState, state => state.analytics);

/** null until the first page arrives. */
export const selectCustomers = createSelector(getState, state => state.customers && Object.values(state.customers));

export const selectCoupons = createSelector(getState, state => Object.values(state.coupons));

export const selectCouponsSearchResults = createSelector(getState, state => state.couponsSearchResults);

export const selectCustomerDataById = (customerId: string): MemoizedSelector<RootState, CustomerData, DefaultProjectorFn<CustomerData>> =>
  createSelector(getState, state => state.customersData[customerId]);

export const selectCustomerEventsById = (
  customerId: string,
): MemoizedSelector<RootState, CustomerEventDto[], DefaultProjectorFn<CustomerEventDto[]>> =>
  createSelector(getState, state => state.customersEvents[customerId]);

export const selectCustomersPaginationData = createSelector(getState, state => state.customersPaginationData);

export const selectCustomersSort = createSelector(getState, state => state.customersSort);

export const selectCustomersSortDirection = createSelector(getState, state => state.customersSortDirection);

export const selectTiersEnabled = createSelector(getState, state => {
  const loyaltyIntegration = Object.values(state.programIntegrations).find(
    pi => pi.integration.name === Program.IntegrationEnum.Jericommerce,
  );
  const cycleType = (loyaltyIntegration?.properties as { cycleType?: string } | undefined)?.cycleType;
  return !!cycleType && cycleType !== 'none';
});

export const selectCustomersEventsPaginationData = createSelector(getState, state => state.customersEventsPaginationData);

export const selectCampaigns = createSelector(getState, state => Object.values(state.campaigns ?? {}));

export const selectLocationBasedCampaigns = createSelector(getState, state =>
  state.campaigns && Object.values(state.campaigns)
    .filter(campaign => campaign.type === CampaignType["Location"])
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
);

export const selectScheduledCampaigns = createSelector(getState, state =>
  state.campaigns && Object.values(state.campaigns)
    .filter(campaign => campaign.type === CampaignType["Scheduled"])
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
);

export const selectLinksCampaigns = createSelector(getState, state =>
  state.campaigns && Object.values(state.campaigns)
    .filter(campaign => campaign.type === CampaignType["Links"])
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
);

export const selectTriggeredBasedCampaigns = createSelector(getState, state =>
  state.campaigns && Object.values(state.campaigns)
    .filter(campaign => campaign.type === CampaignType["Triggered"])
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
);

export const selectCouponCampaignRows = createSelector(selectTriggeredBasedCampaigns, getState, (campaigns, state): CouponCampaignRow[] =>
  campaigns?.map(campaign => {
    const slug = parseClaimCodeLink(campaign.properties?.notification?.link);
    return { ...campaign, coupon: (slug && Object.values(state.coupons).find(coupon => coupon.slug === slug)) || null };
  }),
);

export const selectAnonymousCampaigns = createSelector(getState, state =>
  state.campaigns && Object.values(state.campaigns)
    .filter(campaign => campaign.type === CampaignType["Anonymous"])
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
);

export const selectAnalyticsCampaigns = createSelector(getState, state =>
  Object.values(state.campaigns ?? {})
    .filter(campaign => campaign.type === CampaignType["Scheduled"] || campaign.type === CampaignType["Triggered"])
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
);

/** null until fetched: for the list page, which must not show its empty state while loading. */
export const selectFetchedLocations = createSelector(getState, state => state.locations && Object.values(state.locations));

export const selectLocations = createSelector(selectFetchedLocations, locations => locations ?? []);

export const selectLocation = (id: string): MemoizedSelector<RootState, Location, DefaultProjectorFn<Location>> =>
  createSelector(getState, state => state.locations?.[id]);

export const selectDevices = createSelector(getState, state => Object.values(state.devices));

export const selectDevice = (id: string): MemoizedSelector<RootState, Device, DefaultProjectorFn<Device>> =>
  createSelector(getState, state => state.devices[id]);

export const selectCampaign = (id: string): MemoizedSelector<RootState, CampaignDto, DefaultProjectorFn<CampaignDto>> =>
  createSelector(getState, state => state.campaigns?.[id]);

/** null until fetched: for the list page, which must not show its empty state while loading. */
export const selectFetchedBoosts = createSelector(getState, state =>
  state.boosts && Object.values(state.boosts).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
);

export const selectBoosts = createSelector(selectFetchedBoosts, boosts => boosts ?? []);

export const selectBoost = (id: string): MemoizedSelector<RootState, Boost, DefaultProjectorFn<Boost>> =>
  createSelector(getState, state => state.boosts?.[id]);

export const selectBoostsLoading = createSelector(getState, state => state.boostsLoading);

// An editor opened by URL leaves its skeleton when its campaign cannot be loaded
export const selectCampaignLoadError = (id: string): MemoizedSelector<RootState, EntityLoadError | null, DefaultProjectorFn<EntityLoadError | null>> =>
  createSelector(getState, state => state.campaignLoadErrors[id] ?? null);

// The boost editor reads its boost from the boosts list: missing from the fetched list, or the list failed
export const selectBoostLoadError = (id: string): MemoizedSelector<RootState, EntityLoadError | null, DefaultProjectorFn<EntityLoadError | null>> =>
  createSelector(getState, state => {
    if (state.boostsLoading) return null;
    if (state.boostsLoadFailed) return "failed";
    return state.boosts && !state.boosts[id] ? "notFound" : null;
  });

export const selectBoostOverlapConflictId = createSelector(getState, state => state.boostOverlapConflictId);

export const selectTierBalanceType = createSelector(getState, state => {
  const loyaltyIntegration = Object.values(state.programIntegrations).find(
    pi => pi.integration.name === Program.IntegrationEnum.Jericommerce,
  );
  return (loyaltyIntegration?.properties as { balanceType?: string } | undefined)?.balanceType ?? null;
});

export const selectCoupon = (id: string): MemoizedSelector<RootState, CouponInfoDto, DefaultProjectorFn<CouponInfoDto>> =>
  createSelector(getState, state => state.coupons[id]);

export const selectCouponBySlug = (slug: string): MemoizedSelector<RootState, CouponInfoDto, DefaultProjectorFn<CouponInfoDto>> =>
  createSelector(getState, state => Object.values(state.coupons).find(coupon => coupon.slug === slug));

export const selectCouponStats = (id: string): MemoizedSelector<RootState, CouponCounts, DefaultProjectorFn<CouponCounts>> =>
  createSelector(getState, state => state.couponStats[id]);

export const selectLink = (id: string): MemoizedSelector<RootState, Link, DefaultProjectorFn<Link>> =>
  createSelector(getState, state => state.campaignLinks[id]);

export const selectLocationAddressesByIds = (ids: string[]): MemoizedSelector<RootState, string[], DefaultProjectorFn<string[]>> =>
  createSelector(getState, state =>
    Object.values(state.locations ?? {}).reduce((locations, location) => {
      if (ids.includes(location.id)) locations.push(location.address);
      return locations;
    }, []),
  );

export const selectLocationNameById = (id: string): MemoizedSelector<RootState, string, DefaultProjectorFn<string>> =>
  createSelector(getState, state => state.locations?.[id]?.name);

export const selectLinksUrlsByIds = (ids: string[]): MemoizedSelector<RootState, string[], DefaultProjectorFn<string[]>> =>
  createSelector(getState, state =>
    Object.values(state.campaignLinks).reduce((links, link) => {
      if (ids.includes(link.id)) links.push(link.url);
      return links;
    }, []),
  );

export const selectIntegrationById = (id: string): MemoizedSelector<RootState, Integration, DefaultProjectorFn<Integration>> =>
  createSelector(getState, state => state.integrations.find(integration => id === integration.id));

export const selectIsFeatureEnabled = (feature: IntegrationFeature): MemoizedSelector<RootState, boolean, DefaultProjectorFn<boolean>> =>
  createSelector(getState, state =>
    Object.values(state.programIntegrations)?.some(
      programIntegration =>
        programIntegration.enabledFeatures.includes(feature.featureId) && programIntegration.integrationId === feature.integrationId,
    ),
  );

export const selectIsFeatureEnabledByName = (featureName: string): MemoizedSelector<RootState, boolean, DefaultProjectorFn<boolean>> =>
  createSelector(getState, state =>
    Object.values(state.programIntegrations)?.some(programIntegration => {
      const features = programIntegration?.enabledFeatures?.map(id => state.features[id]);
      return features?.some(feature => feature?.name === featureName);
    }),
  );

export const selectIsFeatureEnabledByNames = (
  IntegrationName: string,
  featureName: string,
): MemoizedSelector<RootState, boolean, DefaultProjectorFn<boolean>> =>
  createSelector(getState, state =>
    Object.values(state.programIntegrations)?.some(programIntegration => {
      const features = programIntegration?.enabledFeatures?.map(id => state.features[id]);
      return programIntegration?.integration?.name === IntegrationName && features?.some(feature => feature?.name === featureName);
    }),
  );

export const selectIsJeriCommerceLoyaltyActive = selectIsFeatureEnabledByNames(Program.IntegrationEnum.Jericommerce, "loyalty");

export const selectIsJeriCommerceRewardsActive = selectIsFeatureEnabledByNames(Program.IntegrationEnum.Jericommerce, "rewards");

export const selectFeatures = createSelector(getState, state => Object.values(state.features));

export const selectCouponsFilters = createSelector(getState, state => state.couponsFilters);

export const selectRewardsView = createSelector(getState, state => state.rewardsView);

export const selectIntegrationByName = (name: string): MemoizedSelector<RootState, Integration, DefaultProjectorFn<Integration>> =>
  createSelector(getState, state => state.integrations.find(integration => integration.name === name));

export const selectProgramIntegrationsByFeature = (
  featureName: "loyalty" | "customer",
): MemoizedSelector<RootState, ProgramIntegration[], DefaultProjectorFn<ProgramIntegration[]>> =>
  createSelector(getState, state =>
    Object.values(state.programIntegrations)
      .filter(integration =>
        integration.enabledFeatures.some(
          featureId => Object.values(state.features).find(feature => feature.id === featureId)?.name === featureName,
        ),
      ),
  );

export const selectProgramIntegrationById = (
  id: string,
): MemoizedSelector<RootState, ProgramIntegration, DefaultProjectorFn<ProgramIntegration>> =>
  createSelector(getState, state => Object.values(state.programIntegrations).find(programIntegration => programIntegration.id === id));

export const selectProgramIntegrationByName = (
  name: string,
): MemoizedSelector<RootState, ProgramIntegration, DefaultProjectorFn<ProgramIntegration>> =>
  createSelector(getState, state =>
    Object.values(state.programIntegrations).find(programIntegration => programIntegration.integration.name === name),
  );

/**
 * Whether the LoyaltyLion integration still keeps the token of the token and secret LoyaltyLion supports only until
 * 10 January 2027; saving an API key clears it. The API returns it masked, so only its presence counts.
 */
export const selectLoyaltyLionHasLegacyToken = createSelector(getState, state => {
  const properties = Object.values(state.programIntegrations).find(
    programIntegration => programIntegration.integration?.name === Program.IntegrationEnum.ShopifyLoyaltyLion,
  )?.properties as { token?: string | null } | undefined;

  return !!properties?.token;
});

export const selectProgramTiers = createSelector(getState, state => state.tiers);

export const selectSegments = createSelector(getState, state => state.segments);

// Groups segments for the multi-select optgroups. Internal segments are grouped by their
// `category` (rendered as category sub-groups); external/integration segments keep their
// integration grouping. The "all customers" segment is excluded here and pinned in the template.
function groupSegmentsForMultiSelect(segments: SegmentDto[]): Record<string, SegmentDto[]> {
  return segments.reduce<Record<string, SegmentDto[]>>((groups, segment) => {
    if (segment.id === SegmentId.all) return groups;

    const key =
      segment.source === SegmentDto.SourceEnum.Internal && segment.category ? segment.category : segment.integrationId;

    const group = groups[key] ? [...groups[key], segment] : [segment];
    group.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name));

    return { ...groups, [key]: group };
  }, {});
}

export const selectSegmentsMultiSelect = createSelector(getState, state => groupSegmentsForMultiSelect(state.segments));

// Jericommerce (internal) segments only, plus the "all customers" segment — excludes third-party
// integration segments. Used where only native segments are offered (e.g. location-based campaigns).
export const selectInternalSegments = createSelector(getState, state =>
  state.segments.filter(segment => segment.source === SegmentDto.SourceEnum.Internal || segment.id === SegmentId.all),
);

export const selectInternalSegmentsMultiSelect = createSelector(selectInternalSegments, groupSegmentsForMultiSelect);

export const selectShopifySegments = createSelector(getState, state => {
  const shopifyIntegrationId = state.integrations.find(
    integration => integration.name === Program.IntegrationEnum.Shopify,
  )?.id;

  return state.segments.filter(
    segment =>
      segment.source === SegmentDto.SourceEnum.External
      && segment.integrationId === shopifyIntegrationId
      && segment.status === SegmentDto.StatusEnum.Ready,
  );
});

export const selectShopifySegmentsMultiSelect = createSelector(selectShopifySegments, groupSegmentsForMultiSelect);

export const selectCampaignSlug = createSelector(getState, state => state.campaignSlug);

export const selectRewards = createSelector(getState, state => Object.values(state.rewardsList ?? {}).sort((a, b) => a.order - b.order));

/** null until fetched, so the rewards page shows its spinner instead of the empty state. */
export const selectRewardListRows = createSelector(selectRewards, getState, (rewards, state): RewardListRow[] | null =>
  state.rewardsList && rewards.map(reward => ({ ...reward, details: state.rewardsDetails[reward.id] ?? null })),
);

export const selectReward = (id: string): MemoizedSelector<RootState, RewardDto, DefaultProjectorFn<RewardDto>> =>
  createSelector(getState, state => state.rewards[id]);

export const selectRewardWithCouponCodeCounts = (id: string): MemoizedSelector<
  RootState, RewardWithCouponCodeCountsDto, DefaultProjectorFn<RewardWithCouponCodeCountsDto>
> => createSelector(getState, state => state.rewardsList?.[id]);

/** null until fetched: for the list page, which must not show its empty state while loading. */
export const selectFetchedEngagements = createSelector(getState, state => state.engagements && Object.values(state.engagements));

export const selectEngagements = createSelector(selectFetchedEngagements, engagements => engagements ?? []);

export const selectActiveEngagements = createSelector(selectEngagements, engagements =>
  engagements.filter(engagement => engagement.active && engagement.programEngagementFlows.some(programFlow => programFlow.active)),
);

export const selectEngagement = (name: string): MemoizedSelector<
  RootState, EngagementFlowWithProgramEngagementFlowDto, DefaultProjectorFn<EngagementFlowWithProgramEngagementFlowDto>
> => createSelector(getState, state => state.engagements?.[name]);

export const selectReferralFlowActive = createSelector(
  selectEngagement(EngagementFlowWithProgramEngagementFlowDto.NameEnum.MemberGetMember),
  flow => !!flow?.programEngagementFlows?.[0]?.active,
);

export const selectTemplateMetadata = createSelector(getState, (state: ProgramState): TemplateMetadata | null => state.templateMetadata);
export const selectTemplateMetadataFailed = createSelector(getState, (state: ProgramState): boolean => state.templateMetadataFailed);

export const selectProgramLanguages = createSelector(
  getState,
  (state: ProgramState): ProgramLanguagesResponseDto | null => state.programLanguages,
);

export const selectEnabledLocales = createSelector(
  getState,
  (state: ProgramState): string[] => state.programLanguages?.locales ?? [],
);

export const selectLoadingTranslations = createSelector(
  getState,
  (state: ProgramState): boolean => state.loadingTranslations,
);

export const selectSectionTranslations = createSelector(
  getState,
  (state: ProgramState): (EntityTranslation | UpsertTranslationDto)[] => state.sectionTranslations,
);

export const selectSectionTranslationsByLocale = (
  locale: string,
): MemoizedSelector<RootState, Record<string, { label: string; content: string }>, DefaultProjectorFn<Record<string, { label: string; content: string }>>> =>
  createSelector(getState, (state: ProgramState) => {
    const localeTranslations = state.sectionTranslations.filter(t => t.locale === locale && t.entityType === "section");
    return localeTranslations.reduce<Record<string, { label: string; content: string }>>((acc, t) => {
      if (!acc[t.entityId]) acc[t.entityId] = { label: "", content: "" };
      if (t.field === "label") acc[t.entityId].label = t.value;
      if (t.field === "content") acc[t.entityId].content = t.value;
      return acc;
    }, {});
  });

/** The loyalty card's front texts translated into `locale`, by field path (`ios.header.label`). */
export const selectWalletFieldTranslationsByLocale = (
  locale: string,
): MemoizedSelector<RootState, Record<string, string>, DefaultProjectorFn<Record<string, string>>> =>
  createSelector(getState, (state: ProgramState) =>
    Object.fromEntries(
      state.sectionTranslations
        .filter(t => t.locale === locale && t.entityType === WALLET_FIELD_ENTITY_TYPE)
        .map(t => [t.field, t.value]),
    ),
  );

export type TranslationStatus = "complete" | "partial" | "empty";

export const selectTranslationStatusByLocale = createSelector(
  getState,
  (state: ProgramState): Record<string, TranslationStatus> => {
    const locales = (state.programLanguages?.locales ?? []).filter(l => l !== state.programLanguages?.defaultLocale);
    const sections = Object.values(state.programSections).flat();

    // The front texts configured on the loyalty card translate with the sections (Google's first-row values excepted)
    const walletFields = walletFieldTranslationSources(state.program).map(({ field }) => field);
    const translatableFields = sections.reduce((count, section) =>
      count + (section.label ? 1 : 0) + (section.content ? 1 : 0), walletFields.length);

    const statusOf = (filled: number): TranslationStatus =>
      filled === 0 ? "empty" : filled >= translatableFields ? "complete" : "partial";

    return Object.fromEntries(
      locales.map(locale => {
        if (translatableFields === 0) return [locale, "complete"];

        const translations = state.sectionTranslations.filter(t => t.locale === locale && t.entityType === "section");
        const walletFieldTranslations = state.sectionTranslations.filter(t => t.locale === locale && t.entityType === WALLET_FIELD_ENTITY_TYPE && t.value);
        const filled = sections.reduce((count, section) => {
          const has = (field: string): number => translations.some(t => t.entityId === section.id && t.field === field && t.value) ? 1 : 0;
          return count + (section.label ? has("label") : 0) + (section.content ? has("content") : 0);
        }, walletFields.filter(field => walletFieldTranslations.some(t => t.field === field)).length);

        return [locale, statusOf(filled)];
      }),
    );
  },
);

export const selectMissingTranslationLocales = (
  sections: SectionDto[],
): MemoizedSelector<RootState, string[], DefaultProjectorFn<string[]>> =>
  createSelector(getState, (state: ProgramState): string[] => {
    const enabledLocales = state.programLanguages?.locales ?? [];
    if (enabledLocales.length <= 1) return [];
    return enabledLocales.filter(locale => {
      const localeTranslations = state.sectionTranslations.filter(
        t => t.locale === locale && t.entityType === "section",
      );
      return sections.some(
        section =>
          !localeTranslations.find(t => t.entityId === section.id && t.field === "label") ||
          !localeTranslations.find(t => t.entityId === section.id && t.field === "content"),
      );
    });
  });

export const selectProfileFormTranslationsByLocale = (
  locale: string,
): MemoizedSelector<RootState, ProfileFormLocaleTexts, DefaultProjectorFn<ProfileFormLocaleTexts>> =>
  createSelector(getState, (state: ProgramState) =>
    state.profileFormTranslations
      .filter(t => t.locale === locale && !!t.entityId)
      .reduce<ProfileFormLocaleTexts>((acc, t) => ({ ...acc, [t.entityId]: { ...acc[t.entityId], [t.field]: t.value } }), {}),
  );

export const selectProfileFormSaveError = createSelector(getState, (state: ProgramState) => state.profileFormSaveError);

export const selectCustomerMetafieldDefinitions = createSelector(getState, (state: ProgramState) => state.customerMetafieldDefinitions);

export const selectLoadingCustomerMetafieldDefinitions = createSelector(
  getState,
  (state: ProgramState) => state.loadingCustomerMetafieldDefinitions,
);

export const selectCustomerMetafieldDefinitionsError = createSelector(
  getState,
  (state: ProgramState) => state.customerMetafieldDefinitionsError,
);

export const selectCreatingCustomerMetafieldDefinition = createSelector(
  getState,
  (state: ProgramState) => state.creatingCustomerMetafieldDefinition,
);

export const selectCreateCustomerMetafieldDefinitionError = createSelector(
  getState,
  (state: ProgramState) => state.createCustomerMetafieldDefinitionError,
);

export interface EarningFlowDisplayConfigTranslation {
  title: string;
  description: string;
  longDescription: string;
  // Optional fields used by custom earning flows. Standard flows leave them
  // empty so the existing translation status logic (which only tracks the
  // three required fields above) keeps working unchanged.
  buttonText?: string;
  url?: string;
}

// buttonText is custom-flow only but participates in translation-status checks
// the same way required fields do — the filter in selectEarningFlowTranslationStatus
// only counts it when the default locale has a value, so standard flows
// (which never fill it) keep their previous 3-field behavior.
// url is excluded because it is propagated verbatim at save time without
// going through the translation API.
const EARNING_FLOW_TRANSLATABLE_FIELDS = ["title", "description", "longDescription", "buttonText"] as const;
const EARNING_FLOW_OPTIONAL_TRANSLATABLE_FIELDS = ["url"] as const;
export const EARNING_FLOW_ALL_TRANSLATABLE_FIELDS = [
  ...EARNING_FLOW_TRANSLATABLE_FIELDS,
  ...EARNING_FLOW_OPTIONAL_TRANSLATABLE_FIELDS,
] as const;

export const selectEarningFlowTranslations = createSelector(
  getState,
  (state: ProgramState): (EntityTranslation | UpsertTranslationDto)[] => state.earningFlowTranslations,
);

export const selectEarningFlowTranslationsByLocale = (
  locale: string,
): MemoizedSelector<
  RootState,
  Record<string, EarningFlowDisplayConfigTranslation>,
  DefaultProjectorFn<Record<string, EarningFlowDisplayConfigTranslation>>
> =>
  createSelector(getState, (state: ProgramState) => {
    const localeTranslations = state.earningFlowTranslations.filter(
      t => t.locale === locale && t.entityType === "earning_flow",
    );
    return localeTranslations.reduce<Record<string, EarningFlowDisplayConfigTranslation>>((acc, t) => {
      if (!acc[t.entityId]) acc[t.entityId] = { title: "", description: "", longDescription: "" };
      if (t.field === "title") acc[t.entityId].title = t.value;
      if (t.field === "description") acc[t.entityId].description = t.value;
      if (t.field === "longDescription") acc[t.entityId].longDescription = t.value;
      if (t.field === "buttonText") acc[t.entityId].buttonText = t.value;
      if (t.field === "url") acc[t.entityId].url = t.value;
      return acc;
    }, {});
  });

export const selectEarningFlowTranslationStatus = (
  programEngagementFlowId: string | null,
): MemoizedSelector<
  RootState,
  Record<string, TranslationStatus>,
  DefaultProjectorFn<Record<string, TranslationStatus>>
> =>
  createSelector(getState, (state: ProgramState): Record<string, TranslationStatus> => {
    const defaultLocale = state.programLanguages?.defaultLocale;
    const locales = (state.programLanguages?.locales ?? []).filter(l => l !== defaultLocale);
    if (!programEngagementFlowId) return Object.fromEntries(locales.map(l => [l, "complete"]));

    // A field only "needs" translating when the default locale has it filled.
    // If the default itself leaves a field empty there is nothing to translate,
    // so other locales shouldn't be flagged partial just for matching that gap.
    const requiredFields = EARNING_FLOW_TRANSLATABLE_FIELDS.filter(field =>
      state.earningFlowTranslations.some(
        t =>
          t.locale === defaultLocale
          && t.entityType === "earning_flow"
          && t.entityId === programEngagementFlowId
          && t.field === field
          && !!t.value,
      ),
    );
    const total = requiredFields.length;

    return Object.fromEntries(
      locales.map(locale => {
        // No default-locale entries in state means translations haven't been
        // persisted yet (right after a fresh create, before the upsert lands).
        // Earning flow text only lives in entity_translations, so an empty
        // total isn't "vacuously complete" — it's "no data, mark empty".
        if (total === 0) return [locale, "empty"];

        const filled = requiredFields.reduce((count, field) => {
          const has = state.earningFlowTranslations.some(
            t =>
              t.locale === locale
              && t.entityType === "earning_flow"
              && t.entityId === programEngagementFlowId
              && t.field === field
              && !!t.value,
          );
          return count + (has ? 1 : 0);
        }, 0);
        const status: TranslationStatus = filled === 0 ? "empty" : filled >= total ? "complete" : "partial";
        return [locale, status];
      }),
    );
  });

export interface RewardTranslation {
  title: string;
  description: string;
}

const REWARD_TRANSLATABLE_FIELDS = ["title", "description"] as const;

export const selectRewardTranslations = createSelector(
  getState,
  (state: ProgramState): (EntityTranslation | UpsertTranslationDto)[] => state.rewardTranslations,
);

export const selectRewardTranslationsByLocale = (
  locale: string,
): MemoizedSelector<
  RootState,
  Record<string, RewardTranslation>,
  DefaultProjectorFn<Record<string, RewardTranslation>>
> =>
  createSelector(getState, (state: ProgramState) => {
    const localeTranslations = state.rewardTranslations.filter(
      t => t.locale === locale && t.entityType === "reward",
    );
    return localeTranslations.reduce<Record<string, RewardTranslation>>((acc, t) => {
      if (!acc[t.entityId]) acc[t.entityId] = { title: "", description: "" };
      if (t.field === "title") acc[t.entityId].title = t.value;
      if (t.field === "description") acc[t.entityId].description = t.value;
      return acc;
    }, {});
  });

export const selectRewardTranslationStatus = (
  rewardId: string | null,
): MemoizedSelector<
  RootState,
  Record<string, TranslationStatus>,
  DefaultProjectorFn<Record<string, TranslationStatus>>
> =>
  createSelector(getState, (state: ProgramState): Record<string, TranslationStatus> => {
    const defaultLocale = state.programLanguages?.defaultLocale;
    const locales = (state.programLanguages?.locales ?? []).filter(l => l !== defaultLocale);
    if (!rewardId) return Object.fromEntries(locales.map(l => [l, "complete"]));

    // A field only "needs" translating when the default locale has it filled.
    // If the default itself leaves a field empty there is nothing to translate,
    // so other locales shouldn't be flagged partial just for matching that gap.
    const requiredFields = REWARD_TRANSLATABLE_FIELDS.filter(field =>
      state.rewardTranslations.some(
        t =>
          t.locale === defaultLocale
          && t.entityType === "reward"
          && t.entityId === rewardId
          && t.field === field
          && !!t.value,
      ),
    );
    const total = requiredFields.length;

    return Object.fromEntries(
      locales.map(locale => {
        // No default-locale entries in state means translations haven't been
        // persisted yet. Reward text only lives in entity_translations, so an
        // empty total isn't "vacuously complete" — it's "no data, mark empty".
        if (total === 0) return [locale, "empty"];

        const filled = requiredFields.reduce((count, field) => {
          const has = state.rewardTranslations.some(
            t =>
              t.locale === locale
              && t.entityType === "reward"
              && t.entityId === rewardId
              && t.field === field
              && !!t.value,
          );
          return count + (has ? 1 : 0);
        }, 0);
        const status: TranslationStatus = filled === 0 ? "empty" : filled >= total ? "complete" : "partial";
        return [locale, status];
      }),
    );
  });

export interface TierTranslation {
  content: string;
}

// Only the description (content) drives the translation status and the
// auto-translate prompt. The tier `name` is the canonical identifier (always
// filled), so counting it would flag every tier as needing translation and
// pop the auto-translate modal even for tiers with no description.
const TIER_TRANSLATABLE_FIELDS = ["content"] as const;

export const selectTierTranslations = createSelector(
  getState,
  (state: ProgramState): (EntityTranslation | UpsertTranslationDto)[] => state.tierTranslations,
);

export const selectTierTranslationsByLocale = (
  locale: string,
): MemoizedSelector<
  RootState,
  Record<string, TierTranslation>,
  DefaultProjectorFn<Record<string, TierTranslation>>
> =>
  createSelector(getState, (state: ProgramState) => {
    const localeTranslations = state.tierTranslations.filter(
      t => t.locale === locale && t.entityType === "tier",
    );
    return localeTranslations.reduce<Record<string, TierTranslation>>((acc, t) => {
      if (!acc[t.entityId]) acc[t.entityId] = { content: "" };
      if (t.field === "content") acc[t.entityId].content = t.value;
      return acc;
    }, {});
  });

export const selectTierTranslationStatus = (
  tierId: string | null,
): MemoizedSelector<
  RootState,
  Record<string, TranslationStatus>,
  DefaultProjectorFn<Record<string, TranslationStatus>>
> =>
  createSelector(getState, (state: ProgramState): Record<string, TranslationStatus> => {
    const defaultLocale = state.programLanguages?.defaultLocale;
    const locales = (state.programLanguages?.locales ?? []).filter(l => l !== defaultLocale);
    if (!tierId) return Object.fromEntries(locales.map(l => [l, "complete"]));

    // A field only "needs" translating when the default locale has it filled.
    // If the default itself leaves a field empty there is nothing to translate,
    // so other locales shouldn't be flagged partial just for matching that gap.
    const requiredFields = TIER_TRANSLATABLE_FIELDS.filter(field =>
      state.tierTranslations.some(
        t =>
          t.locale === defaultLocale
          && t.entityType === "tier"
          && t.entityId === tierId
          && t.field === field
          && !!t.value,
      ),
    );
    const total = requiredFields.length;

    return Object.fromEntries(
      locales.map(locale => {
        // When the default locale has no description filled there is nothing to
        // translate, so the tier is complete — don't flag it or pop the
        // auto-translate modal.
        if (total === 0) return [locale, "complete"];

        const filled = requiredFields.reduce((count, field) => {
          const has = state.tierTranslations.some(
            t =>
              t.locale === locale
              && t.entityType === "tier"
              && t.entityId === tierId
              && t.field === field
              && !!t.value,
          );
          return count + (has ? 1 : 0);
        }, 0);
        const status: TranslationStatus = filled === 0 ? "empty" : filled >= total ? "complete" : "partial";
        return [locale, status];
      }),
    );
  });

export interface CouponTranslation {
  name: string;
}

const COUPON_TRANSLATABLE_FIELDS = ["name"] as const;

export const selectCouponTranslations = createSelector(
  getState,
  (state: ProgramState): (EntityTranslation | UpsertTranslationDto)[] => state.couponTranslations,
);

export const selectCouponTranslationsByLocale = (
  locale: string,
): MemoizedSelector<
  RootState,
  Record<string, CouponTranslation>,
  DefaultProjectorFn<Record<string, CouponTranslation>>
> =>
  createSelector(getState, (state: ProgramState) => {
    const localeTranslations = state.couponTranslations.filter(
      t => t.locale === locale && t.entityType === "coupon",
    );
    return localeTranslations.reduce<Record<string, CouponTranslation>>((acc, t) => {
      if (!acc[t.entityId]) acc[t.entityId] = { name: "" };
      if (t.field === "name") acc[t.entityId].name = t.value;
      return acc;
    }, {});
  });

export const selectCouponTranslationStatus = (
  couponId: string | null,
): MemoizedSelector<
  RootState,
  Record<string, TranslationStatus>,
  DefaultProjectorFn<Record<string, TranslationStatus>>
> =>
  createSelector(getState, (state: ProgramState): Record<string, TranslationStatus> => {
    const defaultLocale = state.programLanguages?.defaultLocale;
    const locales = (state.programLanguages?.locales ?? []).filter(l => l !== defaultLocale);
    if (!couponId) return Object.fromEntries(locales.map(l => [l, "complete"]));

    // Source of truth for "what needs translating" is the entity itself, not
    // the entity_translations table. Older entities may pre-date the default
    // locale being persisted into entity_translations; reading from the entity
    // keeps status accurate in that case.
    const coupon = state.coupons[couponId];
    const couponSource: Record<(typeof COUPON_TRANSLATABLE_FIELDS)[number], string | undefined> = {
      name: coupon?.name,
    };
    const requiredFields = COUPON_TRANSLATABLE_FIELDS.filter(field => !!couponSource[field]);
    const total = requiredFields.length;

    return Object.fromEntries(
      locales.map(locale => {
        if (total === 0) return [locale, "complete"];

        const filled = requiredFields.reduce((count, field) => {
          const has = state.couponTranslations.some(
            t =>
              t.locale === locale
              && t.entityType === "coupon"
              && t.entityId === couponId
              && t.field === field
              && !!t.value,
          );
          return count + (has ? 1 : 0);
        }, 0);
        const status: TranslationStatus = filled === 0 ? "empty" : filled >= total ? "complete" : "partial";
        return [locale, status];
      }),
    );
  });

export interface CampaignTranslation {
  header: string;
  body: string;
}

export type CampaignTranslatableField = "header" | "body";

export const selectCampaignTranslations = createSelector(
  getState,
  (state: ProgramState): (EntityTranslation | UpsertTranslationDto)[] => state.campaignTranslations,
);

export const selectCampaignTranslationsByLocale = (
  locale: string,
): MemoizedSelector<
  RootState,
  Record<string, CampaignTranslation>,
  DefaultProjectorFn<Record<string, CampaignTranslation>>
> =>
  createSelector(getState, (state: ProgramState) => {
    const localeTranslations = state.campaignTranslations.filter(
      t => t.locale === locale && t.entityType === "campaign",
    );
    return localeTranslations.reduce<Record<string, CampaignTranslation>>((acc, t) => {
      if (!acc[t.entityId]) acc[t.entityId] = { header: "", body: "" };
      if (t.field === "header") acc[t.entityId].header = t.value;
      if (t.field === "body") acc[t.entityId].body = t.value;
      return acc;
    }, {});
  });

export const selectCampaignTranslationStatus = (
  campaignId: string | null,
  fields: readonly CampaignTranslatableField[] = ["header", "body"],
): MemoizedSelector<
  RootState,
  Record<string, TranslationStatus>,
  DefaultProjectorFn<Record<string, TranslationStatus>>
> =>
  createSelector(getState, (state: ProgramState): Record<string, TranslationStatus> => {
    const defaultLocale = state.programLanguages?.defaultLocale;
    const locales = (state.programLanguages?.locales ?? []).filter(l => l !== defaultLocale);
    if (!campaignId) return Object.fromEntries(locales.map(l => [l, "complete"]));

    // Source of truth for "what needs translating" is the entity itself, not
    // the entity_translations table. Older campaigns may pre-date the default
    // locale being persisted into entity_translations; reading from the entity
    // keeps status accurate in that case. `body` falls back to relevantText
    // because location-based campaigns store the translatable body there
    // rather than in notification.body.
    const campaign = state.campaigns?.[campaignId];
    const campaignSource: Record<CampaignTranslatableField, string | undefined> = {
      header: campaign?.properties?.notification?.header,
      body: campaign?.properties?.notification?.body || campaign?.properties?.relevantText,
    };
    const requiredFields = fields.filter(field => !!campaignSource[field]);
    const total = requiredFields.length;

    return Object.fromEntries(
      locales.map(locale => {
        if (total === 0) return [locale, "complete"];

        const filled = requiredFields.reduce((count, field) => {
          const has = state.campaignTranslations.some(
            t =>
              t.locale === locale
              && t.entityType === "campaign"
              && t.entityId === campaignId
              && t.field === field
              && !!t.value,
          );
          return count + (has ? 1 : 0);
        }, 0);
        const status: TranslationStatus = filled === 0 ? "empty" : filled >= total ? "complete" : "partial";
        return [locale, status];
      }),
    );
  });

export interface ProgramNotificationsTranslation {
  tierNotificationsMessage: string;
  balanceNotificationsMessage: string;
  rewardAvailableNotificationsMessage: string;
}

export const PROGRAM_NOTIFICATION_TRANSLATABLE_FIELDS = [
  "tierNotificationsMessage",
  "balanceNotificationsMessage",
  "rewardAvailableNotificationsMessage",
] as const;

export type ProgramNotificationField = typeof PROGRAM_NOTIFICATION_TRANSLATABLE_FIELDS[number];

export const selectProgramTranslations = createSelector(
  getState,
  (state: ProgramState): (EntityTranslation | UpsertTranslationDto)[] => state.programTranslations,
);

export const selectProgramTranslationsByLocale = (
  locale: string,
): MemoizedSelector<
  RootState,
  ProgramNotificationsTranslation,
  DefaultProjectorFn<ProgramNotificationsTranslation>
> =>
  createSelector(getState, (state: ProgramState): ProgramNotificationsTranslation => {
    const acc: ProgramNotificationsTranslation = {
      tierNotificationsMessage: "",
      balanceNotificationsMessage: "",
      rewardAvailableNotificationsMessage: "",
    };
    for (const t of state.programTranslations) {
      if (t.locale !== locale || t.entityType !== "program") continue;
      if (PROGRAM_NOTIFICATION_TRANSLATABLE_FIELDS.includes(t.field as ProgramNotificationField)) {
        acc[t.field as ProgramNotificationField] = t.value;
      }
    }
    return acc;
  });

export const selectProgramNotificationsTranslationStatus = createSelector(
  getState,
  (state: ProgramState): Record<string, TranslationStatus> => {
    const locales = (state.programLanguages?.locales ?? []).filter(l => l !== state.programLanguages?.defaultLocale);
    const program = state.program;

    const enabledFields: ProgramNotificationField[] = [];
    if (program?.tierNotifications && program?.tierNotificationsMessage) enabledFields.push("tierNotificationsMessage");
    if (program?.balanceNotifications && program?.balanceNotificationsMessage) enabledFields.push("balanceNotificationsMessage");
    if (program?.rewardAvailableNotifications && program?.rewardAvailableNotificationsMessage) {
      enabledFields.push("rewardAvailableNotificationsMessage");
    }

    return Object.fromEntries(
      locales.map(locale => {
        if (enabledFields.length === 0) return [locale, "complete"];

        const filled = enabledFields.reduce((count, field) => {
          const has = state.programTranslations.some(
            t => t.locale === locale && t.entityType === "program" && t.field === field && !!t.value,
          );
          return count + (has ? 1 : 0);
        }, 0);
        const status: TranslationStatus = filled === 0 ? "empty" : filled >= enabledFields.length ? "complete" : "partial";
        return [locale, status];
      }),
    );
  },
);
